import React, { useEffect, useState } from 'react';
import { unflatten } from 'flat';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Modal, ButtonToolbar, Button, Badge, ModalHeader, ModalFooter, ModalBody } from 'reactstrap';
import { axios } from 'ApiClient';
import MatTableForRedux from '@/shared/tables/table/MatTableToTable';
import formater from '../../../../../../shared/helpers/WVFormatter';
import NewBillPayout from './NewBillPayout';
import CopyToClipboard from '../../../../../../shared/components/CopyToClipboard';
import { getConstant } from '../../../../../../shared/helpers/WVConstants';
import TableCellContextMenu from '../../../../Influencer/Research/Prospects/components/custom/TableCellContextMenu';
import { showModalById } from '../../../../../../redux/reducers/TableReducer';
import { modalId } from '../../modals/RefreshBillModal';
import { modalId as podcastPlacementsModalId } from '../../modals/PodcastPlacementsModal';
import { addNotice } from '../../../../../App/store/store';
import Hook from '../../../../../../shared/hooks';


const columnTransform = {
  payer: {
    title: 'Payer',
    valueFunc: record => (record.adminUserName || ''),
    sort: record => record.adminUserName,
  },
  startDate: {
    title: 'Payment Date (ET)',
    valueFunc: record => formater.formatToEST(moment(record.date || '')).format('MM/DD/YY'),
    sort: record => formater.formatToEST(moment(record.date || '')),
  },
  amount: {
    title: 'Amount',
    valueFunc: record => formater.formatCurrency(Number(record.amount || 0)),
    sort: record => Number(record.amount || 0),
  },
  method: {
    title: 'Method',
    valueFunc: (record) => {
      const paymentMethod = getConstant('bill', 'paymentMethod', {});
      return (record.method && paymentMethod[record.method] ? paymentMethod[record.method] : '-');
    },
    sort: record => record.method,
  },
  status: {
    title: 'Status',
    valueFunc: record => (record.transactionStatus === 'success' ? (<Badge color="primary">Success</Badge>) : (<Badge color="danger">Failure</Badge>)),
    sort: record => record.transactionStatus,
  },
  transactionId: {
    title: 'Transaction Id',
    valueFunc: record => (record.transactionId || ''),
    sort: record => record.transactionId,
  },
  notes: {
    title: 'Notes',
    valueFunc: record => (record.notes || ''),
    sort: record => record.notes,
  },
};

const metaLabel = text => (
  <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: '0.04em' }}>{text}</div>
);

// Admin only: the IO number and the memo (which carries it) are internal.
const PaymentMeta = ({ payments, ioNumber }) => {
  const paymentMethods = getConstant('bill', 'paymentMethod', {});
  return [...payments].sort((a, b) => new Date(b.date) - new Date(a.date)).map(payment => (
    <div key={payment._id || payment.ilTransactionId} className="border rounded p-3 mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <strong style={{ fontSize: '16px' }}>
            {/* Trolley pays in EUR only, the other methods in USD (same rule as the backend memo) */}
            {formater.formatCurrency(Number(payment.amount || 0), payment.method === 'trolley' ? 'EUR' : 'USD')}
          </strong>
          <span className="text-muted ml-2">
            {`${paymentMethods[payment.method] || payment.method || '-'} · ${formater.formatToEST(moment(payment.date || '')).format('MM/DD/YY')}`}
          </span>
        </div>
        {payment.transactionStatus === 'success' ? <Badge color="primary">Success</Badge> : <Badge color="danger">Failure</Badge>}
      </div>
      {ioNumber && (
        <div className="mb-3">
          {metaLabel('IO Number')}
          <CopyToClipboard value={String(ioNumber)}>{`#${ioNumber}`}</CopyToClipboard>
        </div>
      )}
      {payment.podcastShowNames?.length > 0 && (
        <div className="mb-3">
          {metaLabel(`Shows (${payment.podcastShowNames.length})`)}
          {payment.podcastShowNames.map(show => (
            <Badge key={show} pill color="light" className="border mr-2 mb-2 px-3 py-2" style={{ fontSize: '13px', fontWeight: 500 }}>
              {show}
            </Badge>
          ))}
        </div>
      )}
      {metaLabel(payment.method === 'stripe' ? 'Memo sent to Stripe' : 'Memo')}
      {payment.memo ? (
        <div className="bg-light rounded px-3 py-2" style={{ wordBreak: 'break-word' }}>
          <CopyToClipboard value={payment.memo}>{payment.memo}</CopyToClipboard>
        </div>
      ) : (
        <span className="text-muted">No memo recorded for this payment.</span>
      )}
    </div>
  ));
};

const ContextMenuInList = ({
  record,
  meta,
}) => {
  const bill = unflatten(record);
  const [hsLink, setHsLink] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [showPayout, setShowPayout] = useState(false);
  const [showRemoveInvoice, setShowRemoveInvoice] = useState(false);
  const [showPodcastPlacementsModal] = Hook.useShowModalHook(podcastPlacementsModalId);
  const [showSuccessModal] = Hook.useShowModalHook('SuccessModal');
  const dispatch = useDispatch();
  const amountFromInvoice = bill.params.cpmProspect?.invoiceAmount?.amount || 0;
  const bhCpmTotalCosts = bill.params?.bh_cpm_total_cost || 0;
  const dealType = bill.params?.typeOfDeal;
  const dealId = bill.params?.deal
  const podcastIoNumber = bill.params?.dealObject?.podcastIo?.podcast_io_number
    || bill.params?.['dealObject.podcastIo.podcast_io_number']
    || bill.populated?.deal?.params?.podcastIo?.podcast_io_number;
  useEffect(() => {
    const {
      dealObject,
    } = bill.params;
    setHsLink(typeof dealObject !== 'undefined' ? `https://app.hubspot.com/contacts/${window.constants.hubspot.portalId}/deal/${dealObject.hs_object_id}` : null);
  }, [bill]);

  const handleDeleteDialogSubmit = () => {
    axios({
      method: 'post',
      url: '/api/portal/finance/post-delete-bill',
      data: {
        id: record.id,
      },
    }).then(() => {
      if (meta && meta.closeFunction) {
        meta.closeFunction();
      }
    }).catch((error) => {
      console.error(error);
    });
    setShowRemoveInvoice(!showRemoveInvoice);
  };

  const goToPage = (url) => {
    const win = window.open(url, '_blank');
    win.focus();
  };

  const contextMenuOptions = [
    {
      label: 'Send Payment',
      handler: () => setShowPayout(true),
      disabled: !bill.populated?.deal?.params?.authorizedPayment?.summaryValidation || (amountFromInvoice && bhCpmTotalCosts && bhCpmTotalCosts > amountFromInvoice),
    },
    { type: 'divider', label: 'divider1' },
    {
      label: 'View Payment History',
      handler: () => setShowHistory(!showHistory),
    },
  ];

  if (bill.params.payments.length > 0) {
    contextMenuOptions.push({
      label: 'Show Payment Meta',
      handler: () => showSuccessModal({
        title: 'Payment Meta',
        size: 'lg',
        message: <PaymentMeta payments={bill.params.payments} ioNumber={dealType === 'Podcast IO' ? podcastIoNumber : null} />,
      }),
    });
  }

  if (hsLink) {
    contextMenuOptions.push({
      label: 'View HubSpot Deal',
      handler: () => goToPage(hsLink),
    });
  }
  console.log('dealType', dealType, 'podcastIoNumber', podcastIoNumber, 'dealId', dealId);
  if (dealType === 'Podcast IO' && dealId) {
    contextMenuOptions.push({
      label: 'Show Podcast Placement',
      handler: () => showPodcastPlacementsModal({
        dealId,
        ioNumber: podcastIoNumber,
        invoiceNumber: bill.params?.invoiceNumber,
      }),
    });
  }

  contextMenuOptions.push({ type: 'divider', label: 'divider2' });

  contextMenuOptions.push({
    label: 'Update Payment Authorizations',
    handler: async () => {
      try {
        await axios.post('/api/portal/finance/post-deal-validation-prefill', { ids: [bill.params?.deal] });
        dispatch(addNotice({
          message: 'Authorization has been updated',
          type: 'success',
        }));
        if (meta && meta.closeFunction) {
          meta.closeFunction();
        }
      } catch (e) {
        console.log(e);
      }
    },
  });

  contextMenuOptions.push({
    label: 'Edit Bill',
    handler: () => goToPage(`/finance/Bill/${bill.id}`),
  });

  if (bill.params.payments.length === 0) {
    contextMenuOptions.push({
      label: 'Update Bill',
      handler: () => dispatch(showModalById(modalId, { record })),
    });
  }
  if (bill.params.payments.length === 0 && !bill.params.dealObject) {
    contextMenuOptions.push({
      label: 'Remove Bill',
      handler: () => setShowRemoveInvoice(!showRemoveInvoice),
    });
  }

  return (
    <>
      <TableCellContextMenu options={contextMenuOptions} />

      <Modal
        isOpen={showHistory}
        toggle={() => setShowHistory(!showHistory)}
        size="lg"
        style={{ maxWidth: '1200px' }}
      >
        <div className="modal__header">
          <h4 className="text-modal  modal__title">Payment History</h4>
        </div>
        {/* eslint-disable-next-line react/no-danger */}
        <div className="modal__body ltr-support theme-light">
          {showHistory ? (
            <MatTableForRedux
              dataArray={bill.params.payments}
              selectable={false}
              columns={columnTransform}
              version={(new Date()).toString()}
              rowsPerPage={5}
            />
          ) : null}
        </div>
        <ButtonToolbar className="modal__footer">
          <Button color="danger" onClick={() => setShowHistory(!showHistory)}>Close</Button>
        </ButtonToolbar>
      </Modal>

      <Modal isOpen={showRemoveInvoice} toggle={() => setShowRemoveInvoice(!showRemoveInvoice)} className="delete-modal" size="sm">
        <ModalHeader toggle={() => setShowRemoveInvoice(!showRemoveInvoice)} >
          Remove Bill
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete the selected Bill?<br />

        </ModalBody>
        <ModalFooter>
          <Button
            className="btn-sm"
            color="danger"
            onClick={handleDeleteDialogSubmit}
          >
            Delete
          </Button>{' '}
          <Button
            className="btn-sm"
            color="secondary"
            onClick={() => setShowRemoveInvoice(!showRemoveInvoice)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <NewBillPayout
        modal={showPayout}
        bill={record}
        modalTitle="New Payment"
        closeDialog={(isUpdate) => {
          setShowPayout(false);
          if (meta && meta.closeFunction && isUpdate) {
            meta.closeFunction();
          }
        }}
      />
    </>
  );
};

ContextMenuInList.propTypes = {
  record: PropTypes.objectOf(PropTypes.any).isRequired,
  meta: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default ContextMenuInList;
